<!-- @format -->

<template>
	<disabled-text v-if="disabled && pcProps.showDisabledText" :value="value" :params="params" />
	<el-radio-group v-else v-model="value" :disabled="disabled" @change="handelChange">
		<template v-if="pcProps.isTag">
			<el-radio-button v-for="item in params" :key="item.value" :label="item.value" class="tag-radio">
				{{ item.label }}
			</el-radio-button>
		</template>
		<template v-else-if="pcProps.isButton">
			<el-radio-button v-for="item in params" :key="item.value" :label="item.value" :border="pcProps.border">
				{{ item.label }}
			</el-radio-button>
		</template>
		<template v-else>
			<el-radio v-for="item in params" :key="item.value" :label="item.value" :border="pcProps.border">
				{{ item.label }}
			</el-radio>
		</template>
	</el-radio-group>
</template>

<script setup lang="ts">
	import { paramsTypeEnum } from '../../config';
	import { type WidgetConfigType } from '../../types';
	import { getCheckRowsToFormDatas } from '../../utils/user';
	import { flat } from '../../utils/util';
	import DisabledText from './disabledText.vue';
	import { useDictList } from '@/api';
	import { getCache, setCache } from '@/utils/util';
	import { getDataProcessList } from '@/api/factory';

	interface Props {
		modelValue: string;
		pcProps: WidgetConfigType;
		disabled?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		modelValue: '',
	});

	const emit = defineEmits(['update:modelValue']);
	const widgetDatas = inject<any>('widgetDatas', ref([]));
	const setFieldsValue = inject<(fields: Record<string, any>) => void>('setFieldsValue', () => {});
	const flatWigetDatas = computed(() => flat(widgetDatas.value || []));
	const value = computed({
		get() {
			return props.modelValue;
		},
		set(value) {
			emit('update:modelValue', value);
		},
	});

	const dictList = ref<any>([]);
	const params = computed(() =>
		props.pcProps.paramsType &&
		(props.pcProps.paramsType === paramsTypeEnum.引用 || props.pcProps.paramsType === paramsTypeEnum.业务对象)
			? dictList.value
			: props.pcProps.params,
	);

	const getDictList = async (dictType: string) => {
		const cacheRes = getCache('dictCache', dictType);
		if (cacheRes) {
			dictList.value = cacheRes;
		} else {
			const res = await useDictList({ dictType });
			setCache('dictCache', dictType, res);
			dictList.value = res;
		}
	};

	const getDataProcessListData = async (newObjectType, newObjectServer) => {
		const res: any = await getDataProcessList({
			businessCode: newObjectType,
			url: newObjectServer,
		});
		dictList.value = res.map((item) => ({
			...item,
			value: props.pcProps.objectValue ? item[props.pcProps.objectValue] : '',
			label: props.pcProps.objectLabel ? item[props.pcProps.objectLabel] : '',
		}));
	};

	const handelChange = (val: any) => {
		if (props.pcProps.paramsType === paramsTypeEnum.业务对象) {
			if (props.pcProps.linkConfig && props.pcProps.linkConfig.length > 0) {
				const data: any = [];
				data.push(dictList.value.find((obj) => obj.value === val));
				const formDatas = getCheckRowsToFormDatas(props.pcProps.linkConfig, flatWigetDatas.value, data);
				setFieldsValue && setFieldsValue(formDatas);
			}
		}
	};

	watch(
		[
			() => props.pcProps.citeType,
			() => props.pcProps.objectType,
			() => props.pcProps.objectServer,
			() => props.pcProps.objectValue,
			() => props.pcProps.objectLabel,
		],
		([newCiteType, newObjectType, newObjectServer, newObjectLabel, newObjectValue]) => {
			if (props.pcProps.paramsType === paramsTypeEnum.引用) {
				newCiteType && getDictList(newCiteType);
			} else if (props.pcProps.paramsType === paramsTypeEnum.业务对象) {
				newObjectType &&
					newObjectServer &&
					newObjectValue &&
					newObjectLabel &&
					getDataProcessListData(newObjectType, newObjectServer);
			} else {
				dictList.value = props.pcProps.params;
			}
		},
		{
			immediate: true,
		},
	);
</script>

<style scoped lang="scss">
	.show-value {
		display: flex;
		gap: 3px;
		flex-wrap: wrap;
	}

	.tag-radio {
		margin: 5px;

		:deep(.el-radio-button__inner) {
			border-radius: 5px !important;
			padding: 3px 10px;
			border: var(--el-border) !important;
			box-shadow: none !important;
		}
	}
</style>
